/* ==========================================================================
   IRON FIT — "where is your door?"

   Lebanon does not really have postal addresses. An order that says
   "Jdeideh, near the pharmacy" is a phone call from the driver at best and
   a failed delivery at worst, so the customer gets a one-tap way to fill
   the delivery details in from where they are actually standing.

   ONE TAP FILLS THREE THINGS
     Address   the area it resolves to, plus a Google Maps link
     Region    the governorate, selected in EasySell's own dropdown
     Map link  the exact coordinates, appended to the address

   It lives inside EasySell's order form, directly under the Address field.
   It used to be a panel on the cart page with a map on it, which asked the
   question in a different place from the answer: you pinned your door on
   one screen, then a form opened over the top asking for your address as
   though the pin had never happened.

   WHY THE LINK GOES IN THE ADDRESS FIELD
     EasySell's form is four fields — name, phone, address, region — with
     no note and nothing custom. Address is the only free-text one that
     reaches the driver, so that is where the coordinates go.

   WHAT IT WILL NOT DO
     Reverse geocoding resolves an AREA, not a doorway — "Al-Lija,
     Mussaitbeh", not "building 12, third floor". Whatever the customer has
     already typed is kept, and the note under the button asks for the
     building and floor, because the map link is what gets the driver to
     the street and only the customer can get them to the door.

   PRIVACY
     The coordinates are sent to OpenStreetMap's Nominatim service to be
     turned into an address. Nothing else is sent, nothing is stored beyond
     this browser tab, and none of it happens until the button is tapped.

   Everything is optional. No tap, no permission, no answer from the
   geocoder — the order still goes through on the typed address.
   ========================================================================== */
(function () {
  'use strict';

  var KEY = 'ifz-pin';
  var MARK = 'Map: https://maps.google.com/?q=';
  var GEO = 'https://nominatim.openstreetmap.org/reverse';

  var coords = null;     /* {lat, lng} once the customer has tapped */
  var area = '';         /* "Al-Lija, Mussaitbeh" — what the geocoder found */
  var region = '';       /* "LB_JL" — the option value we selected */
  var noteText = '';     /* survives EasySell's re-renders, see inject() */
  var busy = false;

  function save() {
    try {
      sessionStorage.setItem(KEY, JSON.stringify({
        c: coords, a: area, r: region, n: noteText
      }));
    } catch (e) {}
  }
  function restore() {
    try {
      var v = JSON.parse(sessionStorage.getItem(KEY));
      if (v && v.c && typeof v.c.lat === 'number') {
        coords = v.c; area = v.a || ''; region = v.r || ''; noteText = v.n || '';
      }
    } catch (e) {}
  }
  function link() {
    return coords ? MARK + coords.lat.toFixed(6) + ',' + coords.lng.toFixed(6) : '';
  }

  /* ------------------------------------------------------------------
     STYLE

     Carried by the script rather than by a stylesheet, because the thing
     it styles is injected into somebody else's form and the two should
     not be able to ship apart from one another.

     Green, but not the site's acid green. EasySell's popup is white with
     black text, and #35e02a on white is about 1.9:1 — a colour that reads
     as a highlighter smear rather than as words. This is the same hue
     taken dark enough to be read (~5.3:1), with the brand green kept as
     the tint behind it.

     Every property is spelled out. The popup ships its own reset under
     .es-css, and an under-specified button would inherit half of it.
     ------------------------------------------------------------------ */
  var CSS =
    '.ifzloc{' +
      'display:flex;align-items:center;justify-content:center;gap:8px;' +
      'width:100%;margin:6px 0 0;padding:9px 12px;' +
      'border:1px dashed #0a7d2c;border-radius:6px;' +
      'background:rgba(53,224,42,.10);color:#0a7d2c;' +
      'font-family:inherit;font-size:14px;font-weight:700;line-height:1.25;' +
      'text-align:center;cursor:pointer;box-shadow:none;appearance:none;' +
    '}' +
    '.ifzloc:hover:not(:disabled){background:rgba(53,224,42,.18);}' +
    '.ifzloc:disabled{cursor:default;}' +
    /* found: solid, because "done" should not look like "still to do" */
    '.ifzloc.is-set{' +
      'border-style:solid;border-color:#0a7d2c;' +
      'background:#0a7d2c;color:#fff;cursor:default;' +
    '}' +
    '.ifzloc.is-busy{opacity:.75;cursor:default;}' +
    '.ifzloc__i{font-style:normal;font-size:15px;line-height:1;}' +
    '.ifzloc__note{' +
      'margin:5px 0 0;font-size:12px;line-height:1.35;color:#6b6b6b;' +
      'font-family:inherit;font-weight:500;' +
    '}';

  function styleOnce() {
    if (document.getElementById('ifzloc-css')) return;
    var s = document.createElement('style');
    s.id = 'ifzloc-css';
    s.textContent = CSS;
    document.head.appendChild(s);
  }

  /* ------------------------------------------------------------------ */
  function addrField() {
    return document.querySelector('.es-form #es-address, .es-form [name="address"]');
  }
  function regionField() {
    return document.querySelector('.es-form #es-city, .es-form select[name="city"]');
  }

  /* ------------------------------------------------------------------
     THE GOVERNORATE

     EasySell's Region dropdown is Shopify's province list for Lebanon —
     eight options, each an ISO 3166-2 code with an underscore instead of
     the hyphen (LB_JL, LB_BA …). Nominatim returns that same code as
     ISO3166-2-lvl4, so the primary match is exact rather than fuzzy.

     THE NINTH GOVERNORATE. Lebanon split Keserwan-Jbeil out of Mount
     Lebanon in 2017. OpenStreetMap knows about it; Shopify's list does
     not, so anything around Jounieh, Kaslik or Byblos comes back with no
     ISO code at all and would leave the dropdown empty — in one of the
     best catchments the shop has. The name table below is what catches
     that, and it is checked whenever the code lookup comes up empty.

     Order matters in one place: "Baalbek" has to be tested before "Beqaa"
     would otherwise be reached, and both before anything generic.
     ------------------------------------------------------------------ */
  var BY_NAME = [
    [/keserwan|kesrouan|kesrwan|jbeil|jbail|byblos|jounieh|jounie/i, 'LB_JL'],
    [/baalbek|baalbeck|hermel/i,                                     'LB_BH'],
    [/akkar|aakkar/i,                                                'LB_AK'],
    [/beirut|bayrut|beyrouth/i,                                      'LB_BA'],
    [/beqaa|bekaa|biqa|zahle|zahl/i,                                 'LB_BI'],
    [/mount lebanon|mont-liban|jabal lubnan|jabal lebanon/i,         'LB_JL'],
    [/nabatieh|nabatiyeh|nabatiye/i,                                 'LB_NA'],
    [/north|nord|shamal|tripoli|koura|zgharta|batroun/i,             'LB_AS'],
    [/south|sud|janoub|sidon|saida|tyre|sour|jezzine/i,              'LB_JA']
  ];

  function regionCode(addr) {
    var sel = regionField();
    if (!sel) return '';

    function has(v) {
      for (var i = 0; i < sel.options.length; i++) {
        if (sel.options[i].value === v) return true;
      }
      return false;
    }

    /* 1. the exact code, when OSM and Shopify agree on the governorate */
    var iso = addr['ISO3166-2-lvl4'] || '';
    var code = iso.replace('-', '_');
    if (code && has(code)) return code;

    /* 2. otherwise read the name — this is the Keserwan-Jbeil path */
    var hay = [addr.state, addr.county, addr.region, addr.city, addr.town,
               addr.village, addr.municipality].filter(Boolean).join(' ');
    for (var i = 0; i < BY_NAME.length; i++) {
      if (BY_NAME[i][0].test(hay) && has(BY_NAME[i][1])) return BY_NAME[i][1];
    }
    return '';
  }

  /* Road, then neighbourhood, then town — deduplicated, because Nominatim
     often repeats the same place as both city and municipality. */
  function areaOf(addr) {
    var bits = [
      addr.road,
      addr.neighbourhood || addr.suburb || addr.quarter,
      addr.city || addr.town || addr.village || addr.municipality
    ].filter(Boolean);

    var seen = {}, out = [];
    bits.forEach(function (b) {
      var k = b.toLowerCase().replace(/[^a-z؀-ۿ]/g, '');
      if (k && !seen[k]) { seen[k] = 1; out.push(b); }
    });
    return out.join(', ');
  }

  /* Nominatim asks for no more than one call a second and no bulk use. One
     call per customer, per tap, is well inside that. The abort is there so
     a slow answer cannot leave the button saying "Finding you" forever. */
  function lookup(lat, lng) {
    var ctl = window.AbortController ? new AbortController() : null;
    var bail = setTimeout(function () { if (ctl) ctl.abort(); }, 9000);

    return fetch(GEO + '?format=jsonv2&lat=' + lat + '&lon=' + lng +
                 '&zoom=18&addressdetails=1&accept-language=en',
                 ctl ? { signal: ctl.signal } : {})
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (j) { clearTimeout(bail); return (j && j.address) || null; })
      .catch(function () { clearTimeout(bail); return null; });
  }

  /* ------------------------------------------------------------------
     FILLING THE FORM

     Never destructive. Whatever the customer typed stays exactly where it
     is; the area is added only if their text does not already say it, and
     the map link only if it is not already there. Tapping twice, or
     tapping after EasySell has rebuilt the form, cannot produce a field
     with the same thing in it twice.

     Setting .value alone would update the box and not Vue's copy of it, so
     the events are dispatched too: v-model's listeners are ordinary DOM
     listeners and run synchronously. The select needs "change" — that is
     what v-model binds on a dropdown, and it is also what makes EasySell
     recost the shipping for the new region.
     ------------------------------------------------------------------ */
  function fill() {
    if (!coords) return;

    var f = addrField();
    if (f) {
      var v = (f.value || '').trim();
      var low = v.toLowerCase();
      var add = [];

      if (area && low.indexOf(area.toLowerCase()) === -1) add.push(area);
      if (low.indexOf('maps.google.com') === -1) add.push(link());

      if (add.length) {
        f.value = (v ? v + ' — ' : '') + add.join(' — ');
        f.dispatchEvent(new Event('input', { bubbles: true }));
        f.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    var sel = regionField();
    if (sel && region && sel.value !== region) {
      sel.value = region;
      sel.dispatchEvent(new Event('input', { bubbles: true }));
      sel.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  /* ------------------------------------------------------------------ */
  function render(btn) {
    if (busy) {
      btn.className = 'ifzloc is-busy';
      btn.disabled = true;
      btn.innerHTML = '<i class="ifzloc__i" aria-hidden="true">&#128205;</i>' +
                      '<span>Finding you&hellip;</span>';
    } else if (coords) {
      btn.className = 'ifzloc is-set';
      btn.disabled = true;
      btn.innerHTML = '<i class="ifzloc__i" aria-hidden="true">&#10003;</i>' +
                      '<span>' + (area ? 'Location added' : 'Map link added') + '</span>';
    } else {
      btn.className = 'ifzloc';
      btn.disabled = false;
      btn.innerHTML = '<i class="ifzloc__i" aria-hidden="true">&#128205;</i>' +
                      '<span>Use my current location</span>';
    }

    var p = btn.parentNode && btn.parentNode.querySelector('.ifzloc__note');
    if (noteText) {
      if (!p) {
        p = document.createElement('p');
        p.className = 'ifzloc__note';
        btn.parentNode.insertBefore(p, btn.nextSibling);
      }
      p.textContent = noteText;
    } else if (p) {
      p.remove();
    }
  }

  function paint() {
    var btn = document.querySelector('.ifzloc');
    if (btn) render(btn);
  }

  function ask() {
    if (!navigator.geolocation) {
      noteText = 'This phone will not share its location — the address you type is enough.';
      paint();
      return;
    }
    busy = true;
    noteText = '';
    paint();

    navigator.geolocation.getCurrentPosition(function (pos) {
      coords = { lat: pos.coords.latitude, lng: pos.coords.longitude };

      lookup(coords.lat, coords.lng).then(function (addr) {
        if (addr) {
          area = areaOf(addr);
          region = regionCode(addr);
        }
        busy = false;
        noteText = area
          ? 'Please add your building and floor — this is the area, not your door.'
          : 'Map link added. Please type your area, building and floor.';
        if (!region) {
          noteText += ' Choose your region below.';
        }
        save();
        fill();
        paint();
      });
    }, function (err) {
      busy = false;
      noteText = err && err.code === 1
        ? 'Location permission was declined — the address you type is enough.'
        : 'No location this time — the address you type is enough.';
      paint();
    }, { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 });
  }

  /* ------------------------------------------------------------------
     INJECTION

     EasySell's popup is a Vue app. It is built when the button is pressed
     and re-rendered whenever the cart, the region or the validation state
     changes — and a re-render throws away anything that was not in its own
     template, including this button. So injection cannot run once. It runs
     on every mutation and puts the button back if it has gone, which is
     cheap: the first thing it does is a lookup that normally finds the
     button already in place.

     Because the button is rebuilt from module state rather than from the
     DOM, a re-render mid-order comes back saying the same thing it said
     before, note and all.
     ------------------------------------------------------------------ */
  function inject() {
    var f = addrField();
    if (!f) return;

    var col = f.closest('.es-input-column') || f.closest('.es-address-group') || f.parentNode;
    if (!col || col.querySelector('.ifzloc')) return;

    styleOnce();

    var btn = document.createElement('button');
    btn.type = 'button';                 /* never submits the form */
    btn.setAttribute('aria-label', 'Use my current location for delivery');
    btn.addEventListener('click', function (e) {
      e.preventDefault();
      e.stopPropagation();
      if (!coords && !busy) ask();
    });

    /* After the input's own container, so it sits under the box rather
       than beside the pin icon that already belongs to it. */
    var box = col.querySelector('.es-address-container');
    if (box && box.parentNode === col) col.insertBefore(btn, box.nextSibling);
    else col.appendChild(btn);

    render(btn);

    /* A form rebuilt after the location was found comes back blank, so
       put the details back into it as well as the button. */
    if (coords) fill();
  }

  /* ------------------------------------------------------------------
     THE LAST WORD

     A safety net, not the main path. If the customer clears the address
     box and retypes it, the map link goes with it and no mutation fires
     while they type, so nothing would notice.

     EasySell's "BUY IT NOW" is a <button type="button"> — there is no
     submit event to listen for, so listening for one caught nothing. This
     watches for the click instead, on the capture phase, which runs before
     the app's own handler reads the field.
     ------------------------------------------------------------------ */
  function guard() {
    document.addEventListener('click', function (e) {
      var t = e.target;
      if (t && t.closest && t.closest('.es-form') && t.closest('.es-cta')) fill();
    }, true);
  }

  function init() {
    restore();
    inject();
    if (window.MutationObserver) {
      new MutationObserver(inject).observe(document.body, { childList: true, subtree: true });
    }
    guard();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();

  window.IFZPin = {
    link: link,
    coords: function () { return coords; },
    area: function () { return area; },
    region: function () { return region; }
  };
})();
